import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TemplateForm } from './components/TemplateForm';
import { RichTextEditor } from './components/RichTextEditor';
import { useTemplateForm } from './hooks/useTemplateForm';
import { analyzeSchema } from './validation/schemas';
import { ProcessingProvider, useProcessing } from '../../features/processing/context';
import { ProcessingContent } from '../../features/processing/components/ProcessingContent';
import { WebSocketProvider } from '../../api/websocket/WebSocketContext';
import { AnalysisResults } from './components/AnalysisResults';

export const AnalyzeTemplate: React.FC<{ onWsUrlChange: (url: string | null) => void }> = ({ onWsUrlChange }) => {
  const navigate = useNavigate();
  const [showProcessing, setShowProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [processSessionId, setProcessSessionId] = useState(0);
  const {
    form,
    errors,
    handleChange,
    validateForm
  } = useTemplateForm(analyzeSchema);

  // Memoize form to prevent unnecessary re-renders
  const memoizedForm = React.useMemo(() => ({
    from: form.from,
    subject: form.subject,
    body: form.body
  }), [form.from, form.subject, form.body]);

  const { state: processingState, dispatch } = useProcessing();
  const processingStateRef = useRef(processingState);

  // Keep the ref updated with the latest state
  useEffect(() => {
    processingStateRef.current = processingState;
  }, [processingState]);

  // Handle processing completion
  const handleProcessingComplete = useCallback(() => {
    // Use the ref to get the latest state
    const currentState = processingStateRef.current;
    console.log('[AnalyzeTemplate] Processing complete', { result: currentState.result });
    
    setShowProcessing(false); // Always re-enable the submit button
    setShowResults(true);
    
  }, []);

  const handleSubmit = async () => {
    if (validateForm()) {
      console.log('[AnalyzeTemplate] Dispatching RESET_STATE');
      dispatch({ type: 'RESET_STATE' });
      setShowResults(false);
      console.log('[AnalyzeTemplate] Updating processSessionId');
      setProcessSessionId((prev) => {
        const newId = prev + 1;
        console.log('[AnalyzeTemplate] processSessionId updated to', newId);
        return newId;
      });
      console.log('[AnalyzeTemplate] Setting showProcessing to true');
      setShowProcessing(true);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexGrow: 1,
      bgcolor: '#1B2B3A',
      p: 2
    }}>
      <Box sx={{ flex: 1, p: 4, maxWidth: 800, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ 
            mb: 4, 
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Back to Dashboard
        </Button>

        <Paper
          elevation={0}
          sx={{ 
            p: 4,
            backgroundColor: '#243B53',
            borderRadius: '12px',
            color: 'white'
          }}
        >
          <Typography variant="h5" sx={{ mb: 4, color: 'white', fontWeight: 500 }}>
            Analyze Template
          </Typography>

          <TemplateForm
            form={form}
            errors={errors}
            onChange={handleChange}
          />

          <RichTextEditor
            value={form.body}
            onChange={(value) => handleChange('body', value)}
            error={errors.body}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={showProcessing}
            sx={{ 
              mt: 3,
              px: 4,
              py: 1.5,
              backgroundColor: '#B4E33D',
              color: '#1B2B3A',
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#9BC535'
              }
            }}
          >
            {showProcessing ? 'Analyzing...' : 'Analyze Template'}
          </Button>
        </Paper>

        {(showProcessing || Object.keys(processingState.stages).length > 0) && (
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2,
              backgroundColor: 'transparent',
              color: 'white'
            }}
          >
            <ProcessingContent
              key={processSessionId}
              form={memoizedForm}
              processingType="analysis"
              onComplete={handleProcessingComplete}
              onWsUrlChange={onWsUrlChange}
            />
          </Paper>
        )}

        <Collapse in={showResults}>
          <AnalysisResults />
        </Collapse>
      </Box>
    </Box>
  );
};

export default function AnalyzeTemplateWithProvider() {
  const [wsUrl, setWsUrl] = useState<string | null>(null);

  return (
    <WebSocketProvider wsUrl={wsUrl || ''}>
      <ProcessingProvider initialProcessingType="analysis">
        <AnalyzeTemplate onWsUrlChange={setWsUrl} />
      </ProcessingProvider>
    </WebSocketProvider>
  );
} 